import { useEffect, useState, useCallback } from "react";
import { Loader2, Undo2, CheckCircle2 } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";

export default function ImportHistory() {
  const [imports, setImports] = useState(null);
  const [undoingId, setUndoingId] = useState(null);
  const { toast } = useToast();

  const fetchImports = useCallback(async () => {
    const { data } = await api.get("/inventory/imports");
    setImports(data);
  }, []);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const handleUndo = async (importId) => {
    setUndoingId(importId);
    try {
      const { data } = await api.post(`/inventory/imports/${importId}/undo`);
      toast({ title: "Import undone", description: `${data.items_deleted} items, ${data.batches_deleted} batches and ${data.movements_deleted} movements removed` });
      fetchImports();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not undo import", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setUndoingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="import-history-title">Import History</h1>
        <InventorySubNav />

        {imports === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : imports.length === 0 ? (
          <p className="text-amtm-textMuted text-center py-12" data-testid="import-history-empty-state">No workbook imports yet.</p>
        ) : (
          <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="import-history-list">
            {imports.map((imp) => (
              <div key={imp.id} className="flex items-center justify-between p-4 gap-3 flex-wrap" data-testid={`import-history-row-${imp.id}`}>
                <div>
                  <p className="font-semibold text-amtm-text">{imp.file_name}</p>
                  <p className="text-sm text-amtm-textMuted">
                    {formatDate(imp.imported_at)} by {imp.imported_by_name} · {imp.items_created} new items, {imp.batches_created} batches, {imp.skipped} skipped
                  </p>
                  {imp.undone_at && (
                    <p className="text-xs text-amtm-success flex items-center gap-1 mt-1" data-testid={`import-history-undone-badge-${imp.id}`}>
                      <CheckCircle2 className="h-3 w-3" /> Undone {formatDate(imp.undone_at)}
                    </p>
                  )}
                  {!imp.undone_at && imp.block_reason && (
                    <p className="text-xs text-amtm-warning mt-1" data-testid={`import-history-block-reason-${imp.id}`}>{imp.block_reason}</p>
                  )}
                </div>
                {!imp.undone_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!imp.can_undo || undoingId === imp.id}
                    onClick={() => handleUndo(imp.id)}
                    data-testid={`import-history-undo-button-${imp.id}`}
                    className="text-amtm-error border-amtm-error/40 hover:bg-amtm-error/10 disabled:opacity-40"
                  >
                    {undoingId === imp.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5 mr-1.5" />}
                    Undo
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
